export const calculatePips = (value1, value2, instrument) => {
    // Check if it's a JPY pair
    const isJPYPair = instrument?.includes('JPY');
    const multiplier = isJPYPair ? 100 : 10000;
    return ((Math.abs(value1 - value2) * multiplier)).toFixed(1);
};

export const getStorageKeyForLines = (instrument) => {
    if (!instrument) return null;
    return `ntb:copilot:lines:${instrument.toLowerCase()}`;
};

// Calculate normalized width based on volume
export const calculateVolumeWidth = (volume, avgVolume, minVolume, maxVolume) => {
    if (!volume || !avgVolume) return 1; // Default width

    // Ensure max and min volumes are set
    const effectiveMaxVolume = maxVolume || avgVolume * 3;
    
    // Normalize volume between 0.5 and 2.0
    // Use log scale to prevent extreme values from dominating
    const normalizedVolume = Math.log(volume + 1) / Math.log(effectiveMaxVolume + 1);
    
    // Scale between 0.5 (low volume) and 2.0 (high volume)
    return 0.5 + (normalizedVolume * 1.5);
};

// Process volume data for candles
export const processVolumeBasedCandleData = (data) => {
    if (!data || data.length === 0) return { processed: [], stats: {} };
    
    // Calculate volume statistics
    const volumes = data.map(candle => candle.volume || 0).filter(vol => vol > 0);
    const totalVolume = volumes.reduce((sum, vol) => sum + vol, 0);
    const avgVolume = totalVolume / volumes.length || 1;
    const maxVolume = Math.max(...volumes, 1);
    const minVolume = Math.min(...volumes.filter(vol => vol > 0), avgVolume * 0.2);
    
    // Create processed data with width multipliers
    const processed = data.map(candle => {
        const volumeMultiplier = calculateVolumeWidth(
            candle.volume || 0,
            avgVolume,
            minVolume,
            maxVolume
        );
        
        return {
            ...candle,
            volumeMultiplier
        };
    });
    
    return {
        processed,
        stats: {
            avgVolume,
            maxVolume,
            minVolume
        }
    };
};

export const getChartOptions = (darkMode, container) => ({
    width: container?.clientWidth || 800,
    height: container?.clientHeight || 600,
    layout: {
        background: { 
            type: 'solid', 
            color: darkMode ? '#131722' : '#ffffff' 
        },
        textColor: darkMode ? '#d1d4dc' : '#131722',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',
    },
    grid: {
        vertLines: { 
            color: darkMode ? '#2B2B43' : '#E1E1E1' 
        },
        horzLines: { 
            color: darkMode ? '#2B2B43' : '#E1E1E1' 
        },
    },
    crosshair: {
        mode: 0,
        vertLine: {
            width: 1,
            color: darkMode ? '#FFFFFF77' : '#2B2B4399',
            style: 0,
            labelBackgroundColor: darkMode ? '#4e5b85' : '#2962FF',
        },
        horzLine: {
            width: 1,
            color: darkMode ? '#FFFFFF77' : '#2B2B4399',
            style: 0,
            labelVisible: true,
            labelBackgroundColor: darkMode ? '#4e5b85' : '#2962FF',
        },
    },
    rightPriceScale: {
        borderColor: darkMode ? '#2B2B43' : '#E1E1E1',
        scaleMargins: {
            top: 0.1,
            bottom: 0.2,
        },
        format: {
            type: 'price',
            precision: 5,
            minMove: 0.00001,
        },
        textColor: darkMode ? '#d1d4dc' : '#131722',
        borderVisible: true,
        entireTextOnly: true,
    },
    timeScale: {
        borderColor: darkMode ? '#2B2B43' : '#E1E1E1',
        timeVisible: true,
        secondsVisible: false,
        fixLeftEdge: true,
        fixRightEdge: false,
        barSpacing: 8, // Increased spacing for better readability
        rightOffset: 20,
        minBarSpacing: 6, // Increased minimum spacing for better alignment
        maxBarSpacing: 16, // Added maximum spacing to keep consistent width
        rightBarStaysOnScroll: true,
        borderVisible: true,
        visible: true,
        scaleMargins: {
            top: 0.1,
            bottom: 0.25,
        }
    },
}); 